// Pure combine logic — spec §4.4. Moved from packages/client/src/game/
// so the server-side shop state machine can call the exact same math.
// Owns nothing, mutates nothing; the caller decides what to do with
// the result.
//
// Fill counter convention: `fill: 1` is a fresh single copy (1/3
// progress toward next star). Each combine increments fill by 1.
// When fill reaches 3 the unit IMMEDIATELY advances one star:
//   - ★1 → ★2: counter resets to 1/3 at the new star level
//   - ★2 → ★3: counter goes straight to 3/3 (the absolute cap) —
//     ★3 has no further progression, so no fill-up state below the
//     cap exists. Once a unit is ★3 it is permanently maxed.
// `canCombine` rejects any drop targeting a ★3 3/3 unit.
/** Return null if `source` cannot combine into `target` per spec §4.4. */
export function canCombine(source, target) {
    if (source === target)
        return 'same slot';
    if (source.unit.id !== target.unit.id)
        return 'different units';
    if (source.star !== target.star)
        return 'different stars';
    if (target.star === 3 && target.fill === 3)
        return 'target capped at ★3';
    return true;
}
/**
 * Apply the combine, returning the post-state result + animation hints.
 * Caller is responsible for removing the dragged unit from its slot
 * and replacing the drop-target slot with `result.result`.
 *
 * Direction-smart: internally treats whichever of the two units has
 * the HIGHER fill as the "kept" unit (its accumulated investment is
 * preserved); the other contributes +1 fill and is consumed. This
 * means the player can't accidentally lose progress just by dropping
 * a fresh unit onto a filled-up one (or vice versa) — both drop
 * directions produce the same outcome. Both units share the same
 * `unit.id` (enforced by canCombine), so the kept identity is
 * indistinguishable.
 *
 * Throws if the combine is invalid — call `canCombine` first.
 */
export function applyCombine(source, target) {
    const check = canCombine(source, target);
    if (check !== true) {
        throw new Error(`Invalid combine: ${check}`);
    }
    // Pick the higher-fill unit as kept; the other is consumed.
    const [kept, consumed] = source.fill >= target.fill ? [source, target] : [target, source];
    // Refund the consumed unit's wasted fill investment (fill above
    // fresh = 1). Compensates the player for the unavoidable loss when
    // combining two non-fresh units; zero when the consumed one was
    // fresh.
    const refund = Math.max(0, consumed.fill - 1);
    const newHp = Math.max(kept.hp, consumed.hp) + 1;
    const newAtk = Math.max(kept.atk, consumed.atk) + 1;
    const newFill = kept.fill + 1;
    const mergedAccrued = mergeAccrued(kept.accruedBench, consumed.accruedBench);
    // Star-up: fill reached 3 and kept wasn't already ★3. The third
    // tick triggers immediate advancement.
    //   ★1 → ★2: counter resets to 1 at the new star (further combines
    //            possible to climb toward ★3).
    //   ★2 → ★3: counter goes straight to 3 (the cap) — ★3 is max,
    //            no further combines allowed.
    if (newFill >= 3 && kept.star < 3) {
        const newStar = (kept.star + 1);
        const reachedCap = newStar === 3;
        return {
            result: {
                unit: kept.unit,
                star: newStar,
                fill: reachedCap ? 3 : 1,
                hp: newHp,
                atk: newAtk,
                ...(mergedAccrued ? { accruedBench: mergedAccrued } : {}),
            },
            starUp: true,
            tickedDot: 3,
            capped: reachedCap,
            refund,
        };
    }
    // No star-up — just bump fill. At ★3 this can reach fill: 3 (the
    // absolute cap); below ★3 newFill never reaches 3 here because the
    // branch above intercepts.
    const cappedFill = Math.min(newFill, 3);
    return {
        result: {
            unit: kept.unit,
            star: kept.star,
            fill: cappedFill,
            hp: newHp,
            atk: newAtk,
            ...(mergedAccrued ? { accruedBench: mergedAccrued } : {}),
        },
        starUp: false,
        tickedDot: cappedFill,
        capped: kept.star === 3 && cappedFill === 3,
        refund,
    };
}
function mergeAccrued(a, b) {
    if (!a && !b)
        return undefined;
    return {
        hp: Math.max(a?.hp ?? 0, b?.hp ?? 0),
        atk: Math.max(a?.atk ?? 0, b?.atk ?? 0),
    };
}
//# sourceMappingURL=combine.js.map